/**
 * Shared defenses for auto-login credentials that the gateway consumes on first
 * use (OTP callbacks, portal handoff tokens). The credential stays on the URL
 * after login, so anything that loads /login again — the game re-firing the
 * deep link, a back navigation, a remount — submits it a second time. The
 * gateway has already deleted it, so that attempt 401s and drops a player who
 * is signed in and browsing on /failed (ACDEV-27624).
 */

/**
 * `session` keeps the mark inside the tab that ran the login. `local` also
 * covers a credential re-fired into a brand-new webview, where sessionStorage
 * starts empty but the session token in localStorage is still live.
 */
type CredentialScope = 'session' | 'local';

interface SingleUseLoginGuardConfig {
    scope: CredentialScope;
    storageKey: string;
    urlParams: string[];
}

export interface SingleUseLoginGuard {
    /** Keyed by credential, so a later, distinct callback can still run. */
    markAttempted: (credential: string) => void;
    isAttempted: (credential: string) => boolean;
    /**
     * Strips the credential immediately so a reload or a back navigation no
     * longer looks like a fresh auth callback.
     */
    clearParamsFromUrl: () => void;
}

export const createSingleUseLoginGuard = ({
    scope,
    storageKey,
    urlParams,
}: SingleUseLoginGuardConfig): SingleUseLoginGuard => ({
    markAttempted: (credential: string): void => {
        try {
            resolveStorage(scope).setItem(storageKey, credential);
        } catch {
            // private mode / quota — stripping the URL params is the other defense
        }
    },

    isAttempted: (credential: string): boolean => {
        try {
            return resolveStorage(scope).getItem(storageKey) === credential;
        } catch {
            return false;
        }
    },

    clearParamsFromUrl: (): void => {
        if (typeof window === 'undefined') {
            return;
        }
        const url = new URL(window.location.href);
        if (!urlParams.some((param) => url.searchParams.has(param))) {
            return;
        }
        urlParams.forEach((param) => url.searchParams.delete(param));
        window.history.replaceState(null, '', `${url.pathname}${url.search}${url.hash}`);
    },
});

const resolveStorage = (scope: CredentialScope): Storage =>
    scope === 'local' ? localStorage : sessionStorage;
